import React, { useMemo } from 'react';
import { useFarmerFormValidation } from '@applicationKYC/LegalEntityQuestionnaire/validation/useFarmerFormValidation';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import breakpointSizes from '@applicationKYC/LegalEntityQuestionnaire/styled-theme-styles/global-style-variables.style';
import useWindowDimensions from '@applicationKYC/LegalEntityQuestionnaire/Utils/window-size.service';
import { Field, Loader } from '@single-platform/components';
import {
  FarmersQuestionnaireHeader,
  PageSecondaryTextStyles,
  PageSubtitle
} from '@applicationKYC/FarmersQuestionnaire/farmersQuestionnaire.style';
import {
  FormStyles,
  PageHeader
} from '@applicationKYC/LegalEntityQuestionnaire/screens/renderPage/renderPage.style';
import { ButtonSection } from '@applicationKYC/LegalEntityQuestionnaire/components';
import { useSelector } from 'react-redux';
import {
  ButtonSectionWrapperStyle,
  EmphasizedBrandColorText
} from '@applicationKYC/FarmersQuestionnaire/questionnaireSections/formStyles';
import { RadioChipOption } from '@applicationKYC/FarmersQuestionnaire/components';
import {
  numberOfPaymentsOptions,
  paymentAmountOptions
} from '@applicationKYC/FarmersQuestionnaire/constants';
import { selectKycAppState } from '@redux';

const defaultValues = {
  incomingPayments: null,
  numberOfPayments: null
};

export const FarmMonthlyIncomingPaymentPage = ({ onSubmit, isAtBottom }) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { loading, farmerInformation } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const isBank = process.env.REACT_APP_TRADEMARK.includes('Bank');
  const applicationState = useSelector(selectKycAppState);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        incomingPayments: Yup.string().required(t('validation_must_be_one_of')).nullable(),
        numberOfPayments: Yup.string().required(t('validation_must_be_one_of')).nullable()
      }),
    [t]
  );

  const existingValues =
    farmerInformation?.farmPaymentsAndTransactionsSection?.farmMonthlyIncomingPaymentPage;
  const initialValues = { ...defaultValues, ...existingValues };

  const { validationHelpers, getValues, setTouchedOnAll, handleSubmit } = useFarmerFormValidation({
    initialValues,
    validationSchema
  });

  const onHandleSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(
      async (data) => {
        await onSubmit?.(data, 'farmMonthlyIncomingPaymentPage');
      },
      () => setTouchedOnAll()
    )();
  };

  if (loading) return <Loader />;

  const values = getValues();

  return (
    <FormStyles small={width < breakpointSizes.phone}>
      <FarmersQuestionnaireHeader noSubHeader>
        <PageHeader>
          <Trans
            t={t}
            components={{ coloredText: <EmphasizedBrandColorText isBank={isBank} /> }}
            i18nKey={'farmers_incoming_payments_header'}
          />
        </PageHeader>
        <PageSubtitle>{t('farmers_incoming_payments_subtitle')}</PageSubtitle>
      </FarmersQuestionnaireHeader>

      <Field
        Component={RadioChipOption}
        options={paymentAmountOptions}
        chipPadding={0}
        value={values.incomingPayments}
        name="incomingPayments"
        validateOnChange
        validationHelpers={validationHelpers}
        isInModal
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />

      <PageSecondaryTextStyles>{t('farmers_incoming_payments_amount')}</PageSecondaryTextStyles>

      <Field
        Component={RadioChipOption}
        options={numberOfPaymentsOptions}
        name="numberOfPayments"
        value={values.numberOfPayments}
        validateOnChange
        validationHelpers={validationHelpers}
        isInModal
        disabled={applicationState?.state === 'UserFillingApplication' ? false : true}
      />
      <ButtonSectionWrapperStyle isAtBottom={isAtBottom}>
        <ButtonSection
          secondButtonClick={onHandleSubmit}
          secondButtonPrimary
          secondButtonText={
            applicationState?.state === 'UserFillingApplication'
              ? t('button_save_and_continue')
              : t('button_continue')
          }
        />
      </ButtonSectionWrapperStyle>
    </FormStyles>
  );
};
